import { useEffect, useMemo, useRef, useState } from "react";
import { ShieldAlert, ShieldCheck, ShieldQuestion, Trash2, X, type LucideIcon } from "lucide-react";
import { api, categoryColor, formatDate, type Folder, type LeerenEintrag, type LeerenPruefung, type LeerenUrteil } from "../api";
import { fehlerText, toast, useDialog } from "./verwaltung";

const GRUPPEN: { id: LeerenUrteil; label: string; hint: string; Icon: LucideIcon }[] = [
  { id: "spam", label: "Sicher unerwünscht", hint: "Spam-Prüfung oder Modell sind eindeutig", Icon: ShieldAlert },
  { id: "unsicher", label: "Unklar", hint: "Weder eindeutig Spam noch erwünscht", Icon: ShieldQuestion },
  { id: "erwuenscht", label: "Vermutlich erwünscht", hint: "Zum Retten vorgemerkt", Icon: ShieldCheck },
];

/** Modell-Score 0–1 als Prozent, Heuristik mit Vorzeichen: negativ spricht gegen Spam. */
function prozent(v: number | null) { return v === null ? "–" : `${Math.round(v * 100)} %`; }
function punkte(v: number | null) {
  return v === null ? "–" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface Props {
  folder: Folder;
  label: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Leert Unerwünscht oder Papierkorb. Vorher läuft eine frische Prüfung, und
 * was erwünscht aussieht, ist zum Retten vorgemerkt. Endgültiges Löschen
 * braucht einen zweiten Klick — es ist der einzige Eingriff ohne Rückweg.
 */
export function OrdnerLeeren({ folder, label, onClose, onDone }: Props) {
  const [pruefung, setPruefung] = useState<LeerenPruefung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [retten, setRetten] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState<LeerenUrteil | "alle">("alle");
  const [offen, setOffen] = useState<number | null>(null);
  const [bestaetigen, setBestaetigen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const box = useRef<HTMLDivElement | null>(null);

  // Esc nimmt zuerst die Bestätigung zurück, erst dann schließt der Dialog.
  useDialog(box, () => (bestaetigen ? setBestaetigen(false) : onClose()), !busy);

  useEffect(() => {
    setFehler(null);
    api.leerenPruefung(folder.id)
      .then((p) => {
        setPruefung(p);
        setRetten(new Set(p.eintraege.filter((e) => e.urteil === "erwuenscht").map((e) => e.id)));
        // Gibt es Kandidaten zum Retten, zuerst die zeigen.
        if (p.zusammenfassung.erwuenscht > 0) setFilter("erwuenscht");
      })
      .catch((e) => setFehler(fehlerText(e)));
  }, [folder.id, versuch]);

  const sichtbar = useMemo(
    () => (pruefung?.eintraege ?? []).filter((e) => filter === "alle" || e.urteil === filter),
    [pruefung, filter],
  );

  const gesamt = pruefung?.eintraege.length ?? 0;
  const zuLoeschen = gesamt - retten.size;
  const nichtGespiegelt = pruefung?.aufServer != null ? Math.max(0, pruefung.aufServer - gesamt) : 0;

  const umschalten = (id: number) => {
    setBestaetigen(false);
    setRetten((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const ausfuehren = async () => {
    if (!pruefung) return;
    // Endgültiges Löschen braucht einen zweiten Klick; nur Retten nicht.
    if (!bestaetigen && zuLoeschen > 0) { setBestaetigen(true); return; }
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.leeren(folder.id, pruefung.eintraege.map((e) => e.id), [...retten]);
      const text =
        `${r.geloescht.toLocaleString("de-DE")} ${r.geloescht === 1 ? "Mail" : "Mails"} endgültig gelöscht` +
        (r.gerettet ? `, ${r.gerettet} in den Posteingang verschoben` : "") +
        (r.uebersprungen ? `, ${r.uebersprungen} übersprungen (inzwischen verändert)` : "") + ".";
      setErgebnis(text);
      toast.erfolg(`${label}: ${text}`);
      onDone();
    } catch (e) {
      setFehler(fehlerText(e));
      setBestaetigen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={box} className="sheet leeren" role="dialog" aria-modal="true" aria-labelledby="leeren-titel">
        <div className="sheet-head">
          <Trash2 size={16} aria-hidden="true" style={{ color: "var(--danger)" }} />
          <span id="leeren-titel" style={{ flex: 1 }}>{label} leeren</span>
          <button className="icon-btn" onClick={onClose} disabled={busy} aria-label="Dialog schließen" title="Schließen (Esc)">
            <X aria-hidden="true" />
          </button>
        </div>

        <div className="sheet-body">
          {ergebnis ? (
            <div className="v-leer" style={{ borderStyle: "solid" }}>
              <strong>Erledigt</strong>
              <span>{ergebnis}</span>
            </div>
          ) : !pruefung ? (
            fehler ? (
              <div className="v-leer" data-art="fehler">
                <strong>Die Prüfung ist fehlgeschlagen</strong>
                <span>{fehler}</span>
                <button className="btn" onClick={() => setVersuch((v) => v + 1)}>Erneut prüfen</button>
              </div>
            ) : (
              <div className="v-leer" role="status" style={{ borderStyle: "solid" }}>
                <span className="v-laden" data-klein="true" style={{ padding: 0 }} />
                <strong>Prüfe {folder.total.toLocaleString("de-DE")} {folder.total === 1 ? "Mail" : "Mails"} …</strong>
                <span>Die Spam-Prüfung läuft frisch, mit allem, was der Agent inzwischen über die Absender weiß.</span>
              </div>
            )
          ) : gesamt === 0 ? (
            <div className="v-leer">
              <strong>Der Ordner ist leer</strong>
              <span>{nichtGespiegelt ? `${nichtGespiegelt} Mails liegen nur auf dem Server und wurden nicht geprüft.` : "Hier gibt es nichts zu tun."}</span>
            </div>
          ) : (
            <>
              <div className="leeren-gruppen">
                <button className="leeren-gruppe" aria-pressed={filter === "alle"} onClick={() => setFilter("alle")}>
                  <span className="n">{gesamt}</span>
                  <span className="k">Alle geprüft</span>
                </button>
                {GRUPPEN.map(({ id, label: l, hint, Icon }) => (
                  <button key={id} className="leeren-gruppe" data-urteil={id} aria-pressed={filter === id} onClick={() => setFilter(id)} title={hint}>
                    <span className="n"><Icon aria-hidden="true" />{pruefung.zusammenfassung[id]}</span>
                    <span className="k">{l}</span>
                  </button>
                ))}
              </div>

              {nichtGespiegelt > 0 && (
                <p className="hint" style={{ margin: "0 0 12px" }}>
                  {nichtGespiegelt} weitere {nichtGespiegelt === 1 ? "Mail liegt" : "Mails liegen"} nur auf dem Server und
                  {nichtGespiegelt === 1 ? " wurde" : " wurden"} nicht geprüft — {nichtGespiegelt === 1 ? "sie bleibt" : "sie bleiben"} unangetastet.
                </p>
              )}

              <ul className="leeren-liste">
                {sichtbar.map((e) => (
                  <Zeile key={e.id} e={e} gerettet={retten.has(e.id)} offen={offen === e.id}
                    onRetten={() => umschalten(e.id)} onOffen={() => setOffen(offen === e.id ? null : e.id)} />
                ))}
                {sichtbar.length === 0 && <li className="v-zelle-leer" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>In dieser Gruppe ist nichts.</li>}
              </ul>
            </>
          )}
        </div>

        {pruefung && gesamt > 0 && !ergebnis && (
          <div className="sheet-foot">
            <span className="hint" style={{ flex: 1, color: bestaetigen ? "var(--danger)" : undefined }} role={bestaetigen ? "alert" : undefined}>
              {bestaetigen
                ? `Letzte Rückfrage: ${zuLoeschen.toLocaleString("de-DE")} ${zuLoeschen === 1 ? "Mail wird" : "Mails werden"} endgültig gelöscht. Das lässt sich nicht rückgängig machen.`
                : `${zuLoeschen.toLocaleString("de-DE")} löschen${retten.size ? ` · ${retten.size} in den Posteingang retten` : ""}`}
            </span>
            {fehler && <span className="error" style={{ margin: 0 }} role="alert">{fehler}</span>}
            {bestaetigen && <button className="btn quiet" onClick={() => setBestaetigen(false)} disabled={busy}>Doch nicht</button>}
            <button className={bestaetigen ? "btn danger-solid" : "btn danger"} onClick={ausfuehren} disabled={busy || gesamt === 0}>
              {busy ? "Wird ausgeführt …"
                : zuLoeschen === 0 ? `Alle ${retten.size} retten`
                : bestaetigen ? `Ja, ${zuLoeschen.toLocaleString("de-DE")} endgültig löschen`
                : "Leeren …"}
            </button>
          </div>
        )}
        {ergebnis && (
          <div className="sheet-foot">
            <div className="spacer" />
            <button className="btn primary" onClick={onClose}>Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Zeile({ e, gerettet, offen, onRetten, onOffen }: {
  e: LeerenEintrag; gerettet: boolean; offen: boolean; onRetten: () => void; onOffen: () => void;
}) {
  return (
    <li className="leeren-zeile" data-urteil={e.urteil} data-gerettet={gerettet}>
      <label className="leeren-retten" title="In den Posteingang retten statt löschen">
        <input type="checkbox" checked={gerettet} onChange={onRetten} aria-label="Retten" />
      </label>
      <div className="leeren-inhalt">
        <span className="msg-row">
          <span className="msg-from">{e.fromName || e.fromEmail}</span>
          {e.date && <span className="msg-date">{formatDate(e.date)}</span>}
        </span>
        <span className="msg-subject" style={{ display: "block" }}>{e.subject || "(ohne Betreff)"}</span>
        <span className="msg-meta">
          {e.category && <span className="chip" style={{ ["--chip-color" as string]: categoryColor(e.category) }}>{e.category}</span>}
          <span className="chip" title="Einschätzung des Sprachmodells beim Eintreffen">Modell {prozent(e.spamScore)}</span>
          <span className="chip" title="Frische Punktzahl der technischen Spam-Prüfung — ab +0,75 Spam, unter −0,40 erwünscht">
            Prüfung {punkte(e.heuristicScore)}
          </span>
          {e.gruende.slice(0, 1).map((g) => <span key={g} className="chip solid">{g}</span>)}
          {e.signale.length > 0 && (
            <button className="signals-toggle" onClick={onOffen} aria-expanded={offen}>
              {offen ? "▾" : "▸"} {e.signale.length} Befunde
            </button>
          )}
        </span>
        {offen && e.signale.length > 0 && (
          <ul className="signal-list">
            {e.signale.map((s) => (
              <li key={s.label} data-kind={s.weight > 0 ? "verdacht" : "entlastung"}>
                <span className="signal-label">{s.label}</span>
                <span className="signal-detail">{punkte(s.weight)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {gerettet && <span className="leeren-marke">wird gerettet</span>}
    </li>
  );
}
